import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import xhrBackend from 'i18next-xhr-backend';
import * as moment from 'moment';
import { initReactI18next } from 'react-i18next';
import { languageCodeToMomentLocaleFile } from '../constants/dateConstants';
import { IContext } from '../teams-sdk/teamsSdk';

/***
 * regex to match the country-LANGUAGE format
 * eg: en-US, fr-CA, zh-cn
 */
const LOCALE_CODE_REGEX = /^([a-zA-Z]{2,3})-([a-zA-Z]{2})$/;

export interface ILocale {
  language: string;
  country: string;
}

export const parseLangCode = (locale: string): ILocale | null => {
  const match = locale.match(LOCALE_CODE_REGEX);
  let country = '';
  let language = '';

  if (!match) {
    return null;
  }

  country = match[2];
  language = match[1];

  return {
    language,
    country
  };
};

export const toLocaleCase = (locale: string): string => {
  const parsedLocale = parseLangCode(locale);

  if (!parsedLocale) {
    if (locale === 'sr-latn-rs') {
      // sr-latn-rs is a special case language code that doesn't match the regex above.
      return 'sr-Latn-RS';
    }
    return locale;
  }

  return `${parsedLocale.language.toLowerCase()}-${parsedLocale.country.toUpperCase()}`;
};

/***
 * @param context Teams context object
 */
export const setLocaleFromTeamsContext = async (context: IContext) => {
  const locale: string = toLocaleCase(context.locale);
  await i18n
    .use(xhrBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: 'en-US',
      ns: ['CustomerEngagementHub'],
      defaultNS: 'CustomerEngagementHub',
      debug: process.env.NODE_ENV === 'development',
      lowerCaseLng: false,
      saveMissing: true,
      saveMissingTo: 'current',
      // The key is . (period) by default but we are currently using the key as fallback
      // to simplify development
      // https://www.i18next.com/principles/fallback#key-fallback
      // `keySeparator` allows for grouping of keys
      // Example:
      //    t('button.save')
      // results in the following in the json file:
      // "button": { "save": "xxxxxx" }
      keySeparator: false,
      // Similarly, namespace separator (ns) is : by default and allows for a specific ns to be used
      //    t('example-ns:button.save')
      nsSeparator: false,
      interpolation: {
        escapeValue: false // Not needed for react
      },
      react: {
        wait: true
      },
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json'
      }
    });

  const momentLocaleDir = languageCodeToMomentLocaleFile[locale];
  if (momentLocaleDir) {
    await import(`moment/locale/${momentLocaleDir}`);
  }

  moment.locale(locale);
};
